/**
 * Rollbar noise filters.
 *
 * Shared between the client-side `checkIgnore` hook and the server-side
 * `captureError`/`captureMessage` short-circuit. Any message matching one of
 * these patterns is dropped before it reaches Rollbar so it does not trip the
 * "level >= error" GitHub Issues rule for non-actionable noise.
 *
 * When adding a new rule, cite the Rollbar item it came from and the reason
 * it is not a real bug. If the class is ambiguous, prefer downgrading to
 * `warning` at the call site over filtering here.
 *
 * ⛔ **Two hard rules learned from #8121.** A `delinquency-escalation-workflow`
 * run died three times in production on 2026-07-27 having already served three
 * statutory demand letters, and no Rollbar item existed to page anyone. The
 * capture was dropped here, by a browser-noise filter:
 *
 *  1. **Never write a match-anywhere rule.** The rule was `/twilio/i` --
 *     unanchored, case-insensitive, substring -- and the failing message was
 *     ``Failed query: select "sms_enabled" from "org_twilio_compliance" …``.
 *     Our own table name satisfied a filter meant for the Twilio browser SDK.
 *     Anchor on a word boundary, an exact match, or a required neighbouring
 *     token; never a bare substring, and never a bare numeric range.
 *  2. **Scope a rule to the channel it was written for.** Rules for browser-SDK
 *     noise must not sit in front of the server's infrastructure failure
 *     channel, where ~172 Inngest functions report their terminal failures.
 *     That is what the `IgnoreChannel` argument is for -- it is required, not
 *     defaulted, so a new call site has to state which channel it filters.
 *     Anchoring alone was NOT enough here: even `\btwilio\b` still matches
 *     `Cannot find module '@/lib/twilio-compliance/notify'` and
 *     `ECONNRESET connecting to api.twilio.com`, both of which are real
 *     infrastructure failures. Both Twilio rules are therefore client-only.
 *
 * Infrastructure captures additionally opt out of content filtering entirely
 * via `ErrorContext.bypassIgnoreRules` (see `rollbar-server.ts`): whether "a
 * background job died permanently" is reportable is decided by the channel, not
 * by what the message text happens to contain.
 */

/**
 * Stable identifier for each noise rule. Emitted in the server-side
 * `capture SUPPRESSED by ignore-rules:` log line so a future suppression is a
 * one-line diagnosis instead of a source read -- the diagnostic whose absence
 * made #8121 invisible (the short-circuit used to be a bare `return`).
 */
export type IgnoreRuleId =
  | 'twilio-word'
  | 'twilio-error-code'
  | 'safari-load-failed'
  | 'browser-script-error'
  | 'unhandledrejection-no-reason'
  | 'next-redirect'
  | 'next-not-found'
  | 'stale-server-action'
  | 'no-active-session';

/**
 * Which capture channel is being filtered.
 *
 * - `'client'` -- the browser provider's `checkIgnore` hook (`rollbar.ts`).
 * - `'server'` -- the Node provider's capture path (`rollbar-server.ts`), which
 *   carries every Inngest `onFailure`, cron, webhook and money-workflow report.
 *
 * Required on purpose. See rule 2 in the module docblock.
 */
export type IgnoreChannel = 'client' | 'server';

/**
 * Twilio Voice SDK error codes: 20xxx (REST/general) and 31xxx (Device).
 *
 * ⛔ **Deliberately not a bare numeric range** (#8121). The original rule was
 * `/\b(20\d{3}|31\d{3})\b/`, which matched ANY 5-digit token in 20000-20999 or
 * 31000-31999 anywhere in a message. Verified: it suppressed
 * `Failed query: insert into journal_entries … / params: 20500,1` and
 * `Stripe charge failed amount=31499`. Any failure whose message embedded an
 * amount-in-cents of $200.00-$209.99 or $310.00-$319.99 -- or a Postgres
 * `params:` list containing one -- was silently dropped fleet-wide.
 *
 * The code must now be adjacent to an `Error` token (`AcquireError 20101`,
 * `ConnectionError (31005):`, `TwilioError: 31000`), which is the shape every
 * Twilio Device error actually has and which no money message has. Verified
 * against this rule's own existing test corpus, which it still matches in full.
 */
const TWILIO_ERROR_CODE = /Error\b[^\w]{0,3}\(?(20\d{3}|31\d{3})\b/;

/**
 * Twilio SDK errors by name. Handled by VoiceProvider, not application bugs.
 *
 * ⛔ **Word-anchored** since #8121. The prior rule was `/twilio/i` --
 * unanchored, case-insensitive, substring -- and it matched our own
 * `org_twilio_compliance` table name inside a Postgres error, silently dropping
 * three terminal `delinquency-escalation-workflow` failures on 2026-07-27 while
 * three statutory cure clocks were frozen.
 */
const TWILIO_BY_NAME = /\btwilio\b/i;

/**
 * ⛔ The Twilio rules are CLIENT-ONLY (#8121). This is the structural half of
 * the fix, and it is load-bearing beyond the two regex narrowings above.
 *
 * Both Twilio rules exist for BROWSER noise: Twilio Device errors originate in
 * `components/communications/voice-provider.tsx`. On the server they are pure
 * downside, because the server-side Twilio call sites
 * (`lib/twilio-compliance/**`, `lib/twilio-numbers/**`, `lib/twilio/**`) call
 * `captureServerError` DELIBERATELY -- those errors are reports an operator
 * needs, not noise to filter.
 *
 * Word-anchoring alone is NOT sufficient to make them safe on the server, which
 * is why the channel split exists. Verified: `\btwilio\b` still matches
 * `Cannot find module '@/lib/twilio-compliance/notify'` (a real bundling
 * failure) and `ECONNRESET connecting to api.twilio.com` (a real SMS outage),
 * because `/` and `.` are non-word characters that satisfy the boundary. Both
 * are exactly the kind of infrastructure failure the paging channel exists to
 * carry. On the server channel there is no Twilio content rule at all.
 */
const CLIENT_ONLY_RULES: ReadonlyArray<
  readonly [IgnoreRuleId, (msg: string) => boolean]
> = [
  ['twilio-word', (msg) => TWILIO_BY_NAME.test(msg)],
  ['twilio-error-code', (msg) => TWILIO_ERROR_CODE.test(msg)],
];

/**
 * Return the id of the noise rule that suppresses `msg` on `channel`, or `null`
 * when the message should be reported.
 *
 * This is the single rule table. `shouldIgnoreMessage` is a thin boolean
 * wrapper over it so callers that only need a verdict stay unchanged, while the
 * server capture path can name the matching rule in its suppression log line.
 */
export function matchIgnoreRule(
  msg: string,
  channel: IgnoreChannel,
): IgnoreRuleId | null {
  if (!msg) return null;

  // Browser-SDK noise rules. See CLIENT_ONLY_RULES -- these do NOT apply to the
  // server's infrastructure failure channel.
  if (channel === 'client') {
    for (const [id, matches] of CLIENT_ONLY_RULES) {
      if (matches(msg)) return id;
    }
  }

  // Safari's generic error for any aborted fetch (lost connectivity,
  // closed tab mid-request). Environmental, not actionable.
  if (msg === 'Load failed') return 'safari-load-failed';

  // Browser CORS masking: errors thrown by a cross-origin <script> without
  // crossorigin="anonymous" reach window.onerror as ("Script error.", "", 0, 0, null)
  // with no file, line, or stack. Our app code is same-origin (app.paxrent.com) and
  // structurally cannot produce this -- it is always unactionable third-party noise
  // (PostHog CDN bundle from us-assets.i.posthog.com, VGS Collect.js, or browser
  // extension injection). Exact match only: a real error starting "Script error: ..."
  // (with additional detail) still surfaces. Rollbar item #306.
  if (msg === 'Script error.' || msg === 'Script error') {
    return 'browser-script-error';
  }

  // Unhandled promise rejection where `.reason` was not set on the event.
  // Browser quirk, no useful payload to investigate.
  if (/^\[unhandledrejection\] error getting .?reason.? from event/i.test(msg)) {
    return 'unhandledrejection-no-reason';
  }

  // Next.js internal control-flow throws. `redirect()` and `notFound()`
  // work by throwing a tagged Error that the framework catches upstream.
  // Rollbar's `captureUncaught` sees them before Next.js does on some
  // runtimes, producing a 'NEXT_REDIRECT' or 'NEXT_NOT_FOUND' item.
  if (msg === 'NEXT_REDIRECT' || msg.startsWith('NEXT_REDIRECT;')) {
    return 'next-redirect';
  }
  if (msg === 'NEXT_NOT_FOUND') return 'next-not-found';

  // Stale client tab hits a Server Action ID that no longer exists after a
  // deploy. Expected post-deploy churn, not a bug. If a user keeps a tab
  // open across a deploy and then submits a form, the old hashed action ID
  // is not in the new bundle. Next.js surfaces this as a generic error.
  // Next.js emits TWO distinct messages for this one failure class. The second
  // shape is the one actually seen in production (Rollbar items 679/680, both
  // byte-identical), and it was unmatched until #9870 -- so every stale tab
  // filed a fresh GitHub issue (#9863, #9864). Both are fixed, Next.js-internal
  // phrases, not unanchored substrings, per rule 1 of the module docblock; they
  // collide with no other rule in this table.
  //
  // NOT narrowed to the trailing "from an older or newer deployment" clause:
  // Next appends that boilerplate to this error class unconditionally, so it
  // does not discriminate a stale tab from the #7427 redirect-repost shape --
  // it would add regex surface and reduce no risk.
  if (/UnrecognizedActionError/.test(msg) || /Failed to find Server Action/.test(msg)) {
    return 'stale-server-action';
  }

  // Benign server-internal no-session throw from getAuthContext (auth.ts:97)
  // during Next.js prerender / prewarm where no WorkOS session exists.
  // Rollbar item 238: preview-only, 4 occurrences, zero production hits.
  // Real anonymous/expired users are redirected to /sign-in by proxy.ts:596-607
  // before reaching this throw. Genuine auth-attack signals use the separate
  // `auth-suspicious` Rollbar tag, which this filter does not touch.
  // Intentionally exact-match (not /unauthorized/i) so a future different
  // no-session failure class still surfaces.
  if (msg === 'Unauthorized: no active session') return 'no-active-session';

  return null;
}

/**
 * Boolean wrapper over `matchIgnoreRule`. `channel` is required -- see rule 2
 * in the module docblock.
 */
export function shouldIgnoreMessage(
  msg: string,
  channel: IgnoreChannel,
): boolean {
  return matchIgnoreRule(msg, channel) !== null;
}

/**
 * Minimal shape of a Rollbar stack frame read from `payload.body.trace.frames`
 * inside `checkIgnore`. Rollbar populates `filename` (post-mapping) and
 * `filename_unminified` (pre-mapping for items whose maps were applied at
 * ingestion time). We only consume what we need.
 */
export interface RollbarFrame {
  filename?: string;
  method?: string;
  filename_unminified?: string | null;
  method_unminified?: string | null;
}

const REACT_310_SIGNATURE =
  /Minified React error #310|Rendered more hooks than during the previous render/;

// Monorepo app paths. If any frame resolves into one of these, treat the
// error as a genuine application-side hook-rule violation and KEEP it.
// Generated from `ls apps/` on 2026-05-20; update when adding a new app.
const APP_SOURCE_FRAME =
  /apps\/(ai-comparison|focal-point|focal-point-solutions|gilman-recycling|mobile|paxrent-marketing|peloffy|property-management|s2spm|CLGO|devops)\/src\//;

/**
 * Returns true if the captured error matches the known-upstream React 19
 * hook-chain unwind bug surfaced by Next.js App Router's `useActionQueue`
 * (facebook/react#33580). The conditional `use(thenable)` in
 * `node_modules/next/dist/client/components/use-action-queue.ts:139` leaves
 * the work-in-progress hook list incomplete when the suspended render
 * unwinds; the next non-`use` hook in `Router` (`useMemo` at
 * `next/src/client/components/app-router.tsx:168`) then throws #310.
 *
 * Match criteria (ALL must hold):
 *   1. Message contains the React #310 signature.
 *   2. `frames` is a non-empty array (we need stack context to be precise;
 *      a #310 with no frames is too ambiguous to suppress).
 *   3. NO frame's resolved filename (`filename` or `filename_unminified`)
 *      touches any `apps/<app>/src/...` path. If an app frame is present
 *      the throw chain crosses our code, so the violation could be ours
 *      and we KEEP the report so ESLint-evaded conditional hooks surface.
 *
 * Upstream tracking (snapshot 2026-05-20):
 *   - facebook/react#33580 ("Bug: Rendered more hooks ... use(thenable)
 *     after hydration") -- OPEN
 *   - facebook/react#35717 ("Preserve hook chains when unwinding") -- OPEN
 *   - facebook/react#36174 ("Complete partial hook chain on unwind") -- OPEN
 *   - vercel/next.js#78396 -- CLOSED, kicked upstream to React #33580
 *
 * Removal criteria: drop this filter (and revert the matching test cases)
 * after PaxRent upgrades to a Next.js release that ships a React with the
 * hook-chain-unwind fix included, AND a fresh Rollbar items API query
 * confirms no new #310 occurrences fire on the post-upgrade SHA across a
 * one-week soak. See docs/solutions/error-handling/
 * react-310-nextjs-useactionqueue-upstream-20260520.md for the full
 * investigation, the symbolicated stack pulled from Rollbar item
 * 1776621864 that anchored the diagnosis, and the removal procedure.
 */
export function shouldIgnoreReactHookFrameworkBug(
  message: string,
  frames: RollbarFrame[] | null | undefined,
): boolean {
  if (!message || !REACT_310_SIGNATURE.test(message)) return false;
  if (!Array.isArray(frames) || frames.length === 0) return false;
  for (const fr of frames) {
    for (const path of [fr.filename, fr.filename_unminified]) {
      if (typeof path === 'string' && APP_SOURCE_FRAME.test(path)) {
        return false;
      }
    }
  }
  return true;
}

// Exact Stripe.js IntegrationError thrown when an Elements operation
// (confirmSetup / confirmPayment with `elements`) races a torn-down
// PaymentElement iframe. The full message is "We could not retrieve data
// from the specified Element. Please make sure the Element you are
// attempting to use is mounted and the ready event has been emitted." We
// match the stable middle clause so a minor Stripe wording change to the
// trailing sentence does not silently un-suppress.
const STRIPE_ELEMENT_TEARDOWN_SIGNATURE =
  /could not retrieve data from the specified Element/i;

/**
 * Returns true if the captured error is the benign Stripe Elements
 * iframe-teardown `IntegrationError` and the stack is 100% Stripe.js /
 * framework internals (no PaxRent app code).
 *
 * The only Elements surface reachable on /portal/payments
 * (`AddPaymentMethodDialog`) already carries the #3909 `ready`-gate and the
 * #3046 unmount-race guards, so a user-initiated submit cannot trigger this.
 * What remains is an uncaught Stripe.js async promise rejecting AFTER its
 * `PaymentElement` iframe was torn down (in-app browser backgrounding /
 * unmount). Stripe owns that promise, so it escapes our awaited try/catch and
 * Rollbar's global handler files it as an uncaught error. Same class as the
 * #3909 `IntegrationError` but on the residual teardown path the `ready`-gate
 * cannot cover.
 *
 * Match criteria (ALL must hold), mirroring shouldIgnoreReactHookFrameworkBug:
 *   1. Message contains the exact Stripe IntegrationError signature.
 *   2. `frames` is a non-empty array (a message with no stack is too
 *      ambiguous to suppress).
 *   3. NO frame's resolved filename (`filename` or `filename_unminified`)
 *      touches any `apps/<app>/src/...` path. If an app frame is present the
 *      throw crosses our code, so the mount bug could be ours and we KEEP the
 *      report. This is the safety valve: any real app-side Elements mount bug
 *      we introduce keeps surfacing because an app frame defeats the filter.
 *
 * Anchored on Rollbar item 1777367786 (counter 36, production /portal/payments,
 * code_version 3ad64331, Google in-app WebView on iOS 26): 1 occurrence, an
 * uncaught Stripe.js IntegrationError whose entire stack is `js.stripe.com/v3`
 * frames with zero PaxRent frames. This error can never represent a failed
 * actual rent charge: the wizard's saved-method pay path uses
 * `confirmPayment({ payment_method })`, not `elements`, so it does not throw
 * the Element error.
 *
 * Removal criteria: drop this filter (and revert the matching test cases) if
 * occurrences later spike on a specific real (non-in-app-browser) flow with an
 * app frame in the stack -- that would be a genuine mount bug to fix, not noise
 * to widen the filter around.
 */
export function shouldIgnoreStripeElementTeardown(
  message: string,
  frames: RollbarFrame[] | null | undefined,
): boolean {
  if (!message || !STRIPE_ELEMENT_TEARDOWN_SIGNATURE.test(message)) return false;
  if (!Array.isArray(frames) || frames.length === 0) return false;
  for (const fr of frames) {
    for (const path of [fr.filename, fr.filename_unminified]) {
      if (typeof path === 'string' && APP_SOURCE_FRAME.test(path)) {
        return false;
      }
    }
  }
  return true;
}

// WebKit masks any script it cannot attribute to a resolvable, same-origin/
// CORS-visible source -- almost always a browser extension or userscript
// manager injecting code into the page -- by reporting its frame's filename
// as this literal placeholder instead of a real URL. Our own bundled code is
// always attributable, so this can never be an app-code frame.
const WEBKIT_MASKED_URL_FRAME = /^webkit-masked-url:\/\//;

/**
 * Returns true if the captured error has a WebKit-masked (browser-extension
 * or userscript-injected) frame and no frame resolves into our app code.
 *
 * Anchored on Rollbar item 686 (production, 2026-09-08, 1 occurrence):
 * `Error: userscript-state-changed` at `fetchTextFromChunks
 * webkit-masked-url://hidden/:648`. A third-party extension/userscript
 * throwing inside the page, not a PaxRent defect.
 *
 * Deliberately keyed on the FRAME, not the message -- per #8121's rule 1
 * (never a message-substring match) and #9870's lesson (a message-keyed rule
 * only ever matches the one wording it was written for; the next userscript
 * manager throws different text, since there is no shared vocabulary across
 * them, and files a fresh ticket). The WebKit masked-url marker is the actual
 * invariant across ALL browser-extension-injected script errors: it is
 * structurally impossible for `apps/<app>/src/` to appear inside a
 * `webkit-masked-url://` URL, so this cannot mask a real app-code regression.
 *
 * Match criteria (ALL must hold), mirroring the two filters above:
 *   1. At least one frame's resolved filename (`filename` or
 *      `filename_unminified`) matches the WebKit masked-url marker.
 *   2. NO frame's resolved filename touches any `apps/<app>/src/...` path --
 *      the safety valve for OTHER frames in the same stack (this condition is
 *      independent of criterion 1 on this input: a masked-url frame itself
 *      can never satisfy `APP_SOURCE_FRAME`).
 *
 * Deliberately NOT "any error with zero app frames" -- that would be far too
 * broad and would swallow a real bug thrown from a genuinely third-party-
 * hosted script we load ourselves (a CDN bundle, an SDK). Scoping to the
 * WebKit masked-url marker keeps the filter narrow to the one class proven
 * (via item 686) to correctly identify browser-extension/userscript noise.
 *
 * WebKit-specific (Safari and other WebKit-based browsers); Chromium/Firefox
 * extension noise reaching `window.onerror` with a real frame would need a
 * separate marker if it recurs -- no evidence of that class yet.
 *
 * Removal criteria: N/A. This is not a temporary upstream-bug workaround like
 * `shouldIgnoreReactHookFrameworkBug` -- it is a permanent class of noise
 * (third-party code injected into the page will always exist).
 */
export function shouldIgnoreBrowserExtensionFrame(
  frames: RollbarFrame[] | null | undefined,
): boolean {
  if (!Array.isArray(frames) || frames.length === 0) return false;
  let masked = false;
  for (const fr of frames) {
    for (const path of [fr.filename, fr.filename_unminified]) {
      if (typeof path !== 'string') continue;
      if (APP_SOURCE_FRAME.test(path)) return false;
      if (WEBKIT_MASKED_URL_FRAME.test(path)) masked = true;
    }
  }
  return masked;
}
